import type * as React from "react";

import { cx } from "../../lib/cx";
import type { Channel } from "../core/ChannelBadge";
import { Icon, type IconName } from "../core/Icon";
import s from "./MetricCard.module.css";

export interface MetricCardProps extends React.ComponentPropsWithoutRef<"div"> {
  /** Eyebrow en mayúsculas, p. ej. "Escaneos del cartel". */
  label: string;
  /** La cifra ya formateada (`1.284`). */
  value: string | number;
  unit?: string;
  /** Período que cubre la cifra, p. ej. "últimos 30 días". Nunca se omite. */
  period?: string;
  /** Variación porcentual con signo contra la base. */
  delta?: number;
  /** Pisa el texto de la base, p. ej. "vs. mes anterior". */
  deltaLabel?: string;
  icon?: IconName;
  /** Tiñe el icono y la regla con el color fijo del canal. */
  channel?: Channel;
  /** default = blanca con borde, suelta en la página; sunken = tile gris sin borde, para vivir adentro de una `Card`. */
  tone?: "default" | "sunken";
}

/** Un valor medido con su unidad, su período y su variación. La medición es la promesa del producto; esto es su átomo. */
export function MetricCard({
  label,
  value,
  unit,
  period,
  delta,
  deltaLabel,
  icon,
  channel,
  tone = "default",
  className,
  ...rest
}: MetricCardProps) {
  const sube = typeof delta === "number" ? delta >= 0 : null;
  return (
    <div className={cx(s.card, className)} data-channel={channel} data-tone={tone} data-cero={String(value) === "0" || undefined} {...rest}>
      <div className={s.cabecera}>
        <span className={s.eyebrow}>{label}</span>
        {icon ? <Icon name={icon} size={17} className={s.icono} /> : null}
      </div>
      <div className={s.cifra}>
        <span className={s.valor}>{value}</span>
        {unit ? <span className={s.unidad}>{unit}</span> : null}
      </div>
      <span className={s.regla} />
      <div className={s.pie}>
        {sube !== null ? (
          <span className={s.delta} data-sube={sube || undefined}>
            <Icon name={sube ? "trending-up" : "trending-down"} size={13} />
            {(sube ? "+" : "") + delta + "%"}
          </span>
        ) : null}
        <span className={s.periodo}>{deltaLabel || period}</span>
      </div>
    </div>
  );
}
